<template>
  <div>
    <h5 class="mb-4">Video</h5>
    <b-alert show>
      Please note your integration must provide a video player
    </b-alert>
    <b-row>
      <b-col lg="3">
        <b-form-group>
          <cool-label>
            Video Player Dimension
            <span
              v-b-popover.hover.click.right="'Select the different sizes of video that should be allowed to be placed in this zone.'"><span
              class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-form-select v-model="video.player_dimension">
            <option v-for="item in videoPlayerDimensionsOptions"
                    :value="item.value">
              {{ item.text }}
            </option>
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col lg="3">
        <b-form-group>
          <cool-label>
            Video Placement
            <span
              v-b-popover.hover.click.right="'What is the placement of this video in the content.'"><span
              class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-form-select v-model="video.placement">
            <option v-for="item in videoPlacements"
                    :value="item.value">
              {{ item.text }}
            </option>
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col lg="6">
        <b-row>
          <b-col sm="6">
            <b-form-group>
              <cool-label>
                Min Play Time
                <span
                  v-b-popover.hover.click.right="'What is the shortest video that should be placed in this zone?'"><span
                  class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
              </cool-label>
              <b-input-group append="seconds">
                <b-form-input
                  type="number"
                  v-model="video.min_duration"
                  placeholder="0">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col sm="6">
            <b-form-group>
              <cool-label>
                Max Play Time
                <span
                  v-b-popover.hover.click.right="'What is the longest video that should be placed in this zone?'"><span
                  class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
              </cool-label>
              <b-input-group append="seconds">
                <b-form-input
                  type="number"
                  v-model="video.max_duration"
                  placeholder="30">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <cool-label>
            Video Linearity
            <span
              v-b-popover.hover.click.right="'Should the video play by itself or in the same space as other videos?'"><span
              class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-form-radio-group
            v-model="video.linear"
            class="small">
            <b-form-radio
              v-for="item in videoLinearityOptions"
              class="my-2"
              :key="item.value"
              :value="item.value"
              :class="{'text-muted': video.linear != item.value}">
              {{ item.text }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
      <b-col>
        <b-row no-gutters>
          <b-col>
            <b-form-group
              label="Ad Pod"
              description="Enable ad pod">
              <toggle-button
                v-model="video.adpod"
                :sync="true"
                color="var(--primary)">
              </toggle-button>
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group label="Pod Duration" :disabled="!video.adpod">
              <b-input-group append="seconds">
                <b-form-input
                  type="number"
                  v-model="video.adpod_duration"
                  placeholder="120">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="6">
        <b-form-group>
          <cool-label>
            Video Play Method
            <span v-b-popover.hover.click.right="'How should the video determine when to start playing?'"><span
              class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-form-radio-group
            v-model="video.autoplay"
            class="small">
            <b-form-radio v-for="item in videoPlayMethodOptions"
                          :key="item.value"
                          :value="item.value"
                          :class="{'text-muted': video.autoplay !== item.value }">
              {{ item.text }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Skippable"
          description="Ad is skippable">
          <toggle-button
            v-model="video.skippable"
            :sync="true"
            color="var(--primary)">
          </toggle-button>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <cool-label>
            APIs
            <span v-b-popover.hover.click.right="'Which APIs should this zone support? (optional)'"><span
              class="icon icon-Tooltip h5 d-inline align-text-bottom"></span></span>
          </cool-label>
          <b-form-checkbox-group
            v-model="video.apis"
            class="small">
            <b-row>
              <b-col xl="4" md="6" class="my-2" v-for="(item, index) in apiOptions" :key="index">
                <b-form-checkbox
                  :class="video.apis && video.apis.indexOf(item.value) === -1 ? 'text-muted' : ''"
                  :value="item.value">
                  {{ item.text }}
                </b-form-checkbox>
              </b-col>
            </b-row>
          </b-form-checkbox-group>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <cool-label>
            RSS Feed For Pre-Roll Content
            <span
              v-b-popover.hover.click.right="'Optional. RSS feed link to generate video content for preroll video player from site post content'"></span></span>
          </cool-label>
          <b-input-group append="URL">
            <b-form-input
              type="text"
              v-model="video.rss_url"
              placeholder="https://site.com/rss">
            </b-form-input>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>


<script type="text/javascript">
    export default {
        props: {
            value: {required: true, type: Object},
            medium: {required: true}
        },
        mounted() {
        },
        data() {
            return {
                videoPlayerDimensionsOptions: [
                    {text: '480 x 270 (Outstream Lg)', value: [480,270]},
                    {text: '300 x 169 (Outstream Sm)', value: [300,169]},
                    {text: '320 x 480 (App Vertical)', value: [320,480]},
                    {text: '480 x 320 (App Horizontal)', value: [480,320]},
                    {text: '300 x 250 (In Banner)', value: [300,250]},
                    {text: '1920 x 1080 (HD)', value: [1920, 1080]},
                    {text: '1 x 1 (Adaptive)', value: [1, 1]}
                ],
                videoLinearityOptions: [
                    {text: 'In-Stream', value: true},
                    {text: 'Out-Stream', value: false}
                ],
                videoPlayMethodOptions: [
                    {value: 'AUTO_SOUNDON', text: 'Autoplay Sound On'},
                    {value: 'AUTO_SOUNDOFF', text: 'Autoplay Sound Off'},
                    {value: 'MOUSEOVER', text: 'Play On Mouse Over'},
                    {value: 'CLICK', text: 'Click To Play'},
                    {value: 'VIEW_SOUNDON', text: 'Enter Viewport Sound On'},
                    {value: 'VIEW_SOUNDOFF', text: 'Enter Viewport Sound OFF'}
                ],
              videoPlacements: [
                {value: 'INSTREAM', text: 'In Stream'},
                {value: 'INBANNER', text: 'In Banner'},
                {value: 'INARTICLE', text: 'In Article'},
                {value: 'INFEED', text: 'In Feed'},
                {value: 'INTERSTITIAL', text: 'Interstitial'},
              ],
                apiOptions: [
                    {text: 'VPAID 1', value: 'VPAID1'},
                    {text: 'VPAID 2', value: 'VPAID2'},
                ]
            }
        },
        computed: {
            video() {
                return this.value
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/publisher/pages/Zone/ZoneSettings/AdFormats/VideoOptions.vue